import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useCallback } from "react";

import { Meta } from "@/@types/paginated";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTaxWorkflowFilterActions } from "../../filter/hooks/actions";
import { useFilterState } from "../../filter/hooks/filterState";

const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50] as const;

interface DataTablePaginationProps {
  meta?: Meta;
}

export function DataTablePagination({ meta }: DataTablePaginationProps) {
  const { filters } = useFilterState();
  const { updateFilter } = useTaxWorkflowFilterActions();

  const goToPage = useCallback(
    (page: number) => {
      updateFilter({
        key: "meta",
        value: {
          ...filters.meta,
          page,
        },
      });
    },
    [filters.meta, updateFilter],
  );

  const handlePerPageChange = useCallback(
    (value: string) => {
      updateFilter({
        key: "meta",
        value: {
          perPage: parseInt(value),
          page: 1,
        },
      });
    },
    [updateFilter],
  );

  const goToFirstPage = useCallback(() => goToPage(1), [goToPage]);
  const goToPreviousPage = useCallback(
    () => goToPage(filters.meta.page - 1),
    [goToPage, filters.meta.page],
  );
  const goToNextPage = useCallback(
    () => goToPage(filters.meta.page + 1),
    [goToPage, filters.meta.page],
  );
  const goToLastPage = useCallback(
    () => goToPage(meta?.lastPage ?? 1),
    [goToPage, meta?.lastPage],
  );

  if (!meta) return null;

  const isFirstPage = meta.prev === null;
  const isLastPage = meta.next === null;

  return (
    <div className="flex items-center justify-between px-2">
      <div className="flex-1 text-sm text-muted-foreground">
        {meta.total} registro(s) encontrado(s).
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Linhas por página</p>
          <Select value={`${meta.perPage}`} onValueChange={handlePerPageChange}>
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={meta.perPage} />
            </SelectTrigger>
            <SelectContent side="top">
              {PAGE_SIZE_OPTIONS.map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[100px] items-center justify-center text-sm font-medium">
          Página {meta.currentPage} de {meta.lastPage}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={goToFirstPage}
            disabled={isFirstPage}
          >
            <span className="sr-only">Ir para a primeira página</span>
            <ChevronsLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={goToPreviousPage}
            disabled={isFirstPage}
          >
            <span className="sr-only">Ir para a página anterior</span>
            <ChevronLeft />
          </Button>
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            onClick={goToNextPage}
            disabled={isLastPage}
          >
            <span className="sr-only">Ir para a próxima página</span>
            <ChevronRight />
          </Button>
          <Button
            variant="outline"
            className="hidden h-8 w-8 p-0 lg:flex"
            onClick={goToLastPage}
            disabled={isLastPage}
          >
            <span className="sr-only">Ir para a última página</span>
            <ChevronsRight />
          </Button>
        </div>
      </div>
    </div>
  );
}
